/**
 * Auto-Visualization Engine
 * Analyzes SQL query results and suggests the most appropriate chart type
 */

export function detectVisualizationType(data, sql, columns) {
  if (!data || data.length === 0) {
    return { type: 'none', reason: 'No data to visualize' };
  }

  if (!columns || columns.length === 0) {
    return { type: 'table', reason: 'No columns detected', config: {} };
  }

  const sqlLower = sql ? sql.toLowerCase() : '';
  // const columnCount = columns.length;
  const rowCount = data.length;

  // 1. Correlation Detection (2 numeric columns)
  if (isCorrelationQuery(sqlLower, columns, data)) {
    return {
      type: 'scatter',
      reason: 'Two numeric attributes detected - ideal for correlation analysis',
      config: {
        xKey: columns[0],
        yKey: columns[1],
        xLabel: formatColumnName(columns[0]),
        yLabel: formatColumnName(columns[1])
      }
    };
  }

  // 2. Distribution Detection (1 numeric column, many rows)
  if (isDistributionQuery(columns, data, rowCount)) {
    return {
      type: 'histogram',
      reason: 'Single numeric attribute with many values - showing distribution',
      config: {
        dataKey: columns[0],
        xLabel: formatColumnName(columns[0]),
        bins: Math.min(20, Math.ceil(Math.sqrt(rowCount)))
      }
    };
  }

  // 3. Comparison Detection (categorical + numeric, GROUP BY)
  if (isComparisonQuery(sqlLower, columns, data)) {
    const categoricalCol = findCategoricalColumn(columns, data);
    const numericCols = findNumericColumns(columns, data);
    
    // Skip visualization if categorical column is athlete names (too many unique values)
    const uniqueValues = new Set(data.map(row => row[categoricalCol])).size;
    if (uniqueValues > 15 || categoricalCol.includes('name')) {
      return {
        type: 'table',
        reason: 'Too many categories or individual records - better viewed as table',
        config: {}
      };
    }
    
    return {
      type: 'bar',
      reason: 'Categorical grouping with numeric values - ideal for comparison',
      config: {
        xKey: categoricalCol,
        dataKeys: numericCols,
        xLabel: formatColumnName(categoricalCol),
        yLabel: 'Value'
      }
    };
  }

  // 4. Multi-Attribute Profile (multiple numeric columns, few rows)
  if (isProfileQuery(columns, data, rowCount)) {
    return {
      type: 'radar',
      reason: 'Multiple attributes for few entities - showing profile comparison',
      config: {
        attributes: findNumericColumns(columns, data),
        athletes: data.map((row, i) => ({
          id: i,
          name: row.full_name || row.name || `Row ${i + 1}`,
          ...row
        }))
      }
    };
  }

  // 5. Ranking/Top-N (ORDER BY with LIMIT)
  if (isRankingQuery(sqlLower, rowCount)) {
    return {
      type: 'bar',
      reason: 'Ranked results - showing top performers',
      config: {
        xKey: columns[0],
        dataKeys: findNumericColumns(columns, data),
        xLabel: formatColumnName(columns[0]),
        horizontal: true
      }
    };
  }

  // 6. Geographic/District Analysis
  if (isGeographicQuery(sqlLower, columns)) {
    return {
      type: 'bar',
      reason: 'Geographic grouping detected',
      config: {
        xKey: findGeographicColumn(columns),
        dataKeys: findNumericColumns(columns, data),
        xLabel: 'Location'
      }
    };
  }

  // Default: Table view
  return {
    type: 'table',
    reason: 'Complex data structure - best viewed as table',
    config: {}
  };
}

// Detection helper functions

function isCorrelationQuery(sql, columns, data) {
  // Two numeric columns
  if (columns.length !== 2) return false;
  
  const numericCols = findNumericColumns(columns, data);
  if (numericCols.length !== 2) return false;
  
  // Check if query mentions correlation keywords
  const correlationKeywords = ['vs', 'versus', 'correlation', 'relationship', 'compare'];
  return correlationKeywords.some(keyword => sql.includes(keyword)) || numericCols.length === 2;
}

function isDistributionQuery(columns, data, rowCount) {
  // Single numeric column with many rows
  if (columns.length !== 1) return false;
  if (rowCount < 10) return false;
  
  const numericCols = findNumericColumns(columns, data);
  return numericCols.length === 1;
}

function isComparisonQuery(sql, columns, data) {
  // Has GROUP BY or categorical + numeric columns
  if (sql.includes('group by')) return true;
  
  const categoricalCol = findCategoricalColumn(columns, data);
  const numericCols = findNumericColumns(columns, data);
  
  return categoricalCol && numericCols.length > 0;
}

function isProfileQuery(columns, data, rowCount) {
  // Multiple numeric columns, few rows (< 10)
  if (rowCount > 10) return false;
  
  const numericCols = findNumericColumns(columns, data);
  return numericCols.length >= 3;
}

function isRankingQuery(sql, rowCount) {
  // Has ORDER BY and LIMIT with small result set
  return sql.includes('order by') && sql.includes('limit') && rowCount <= 20;
}

function isGeographicQuery(sql, columns) {
  const geoKeywords = ['district', 'area', 'town', 'city', 'region', 'location'];
  return geoKeywords.some(keyword => 
    sql.includes(keyword) || columns.some(col => col.toLowerCase().includes(keyword))
  );
}

// Column analysis helpers

function findNumericColumns(columns, data) {
  if (!data || data.length === 0) return [];
  
  return columns.filter(col => {
    const value = data[0][col];
    return typeof value === 'number' || (!isNaN(parseFloat(value)) && isFinite(value));
  });
}

function findCategoricalColumn(columns, data) {
  if (!data || data.length === 0) return null;
  
  const categorical = columns.find(col => {
    const value = data[0][col];
    return typeof value === 'string' && isNaN(parseFloat(value));
  });
  
  return categorical || columns[0];
}

function findGeographicColumn(columns) {
  const geoKeywords = ['district', 'area', 'town', 'city', 'region', 'location'];
  return columns.find(col => 
    geoKeywords.some(keyword => col.toLowerCase().includes(keyword))
  ) || columns[0];
}

function formatColumnName(column) {
  return column
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
    .replace(/Cm|Sec|Kg/g, match => match.toLowerCase());
}

/**
 * Suggest follow-up queries based on current results
 */
export function suggestFollowUpQueries(data, sql, columns) {
  const suggestions = [];
  const sqlLower = sql.toLowerCase();

  // If showing top results, suggest bottom results
  if (sqlLower.includes('order by') && sqlLower.includes('desc')) {
    suggestions.push({
      text: "Show the bottom performers",
      hint: "Change DESC to ASC in the query"
    });
  }

  // If showing single attribute, suggest correlations
  if (columns.length === 1) {
    suggestions.push({
      text: "Find correlations with other attributes",
      hint: "Add another numeric column to see relationships"
    });
  }

  // If showing specific district, suggest comparison
  if (sqlLower.includes('district') && !sqlLower.includes('group by')) {
    suggestions.push({
      text: "Compare across all districts",
      hint: "Use GROUP BY district to see patterns"
    });
  }

  // If showing athletes, suggest similar athletes
  if (data.length > 0 && (data[0].full_name || data[0].name)) {
    suggestions.push({
      text: "Find similar athletes",
      hint: "Use similarity search to find comparable profiles"
    });
  }

  // If showing performance metrics, suggest sport fit
  const performanceKeywords = ['sprint', 'jump', 'height', 'weight', 'strength'];
  if (performanceKeywords.some(keyword => sqlLower.includes(keyword))) {
    suggestions.push({
      text: "Calculate sport probability scores",
      hint: "See which sports these athletes are best suited for"
    });
  }

  return suggestions.slice(0, 3); // Return top 3 suggestions
}

/**
 * Detect if correlation analysis would be valuable
 */
export function shouldShowCorrelationInsights(data, columns) {
  const numericCols = findNumericColumns(columns, data);
  
  // Show correlation insights if we have 2+ numeric columns
  return numericCols.length >= 2;
}

/**
 * Prepare data for correlation analysis
 */
export function prepareCorrelationData(data, columns) {
  const numericCols = findNumericColumns(columns, data);
  
  if (numericCols.length < 2) return null;

  // Return first two numeric columns for correlation
  return {
    attribute1: numericCols[0],
    attribute2: numericCols[1],
    data: data.map(row => ({
      x: parseFloat(row[numericCols[0]]),
      y: parseFloat(row[numericCols[1]]),
      name: row.full_name || row.name || ''
    })).filter(d => !isNaN(d.x) && !isNaN(d.y))
  };
}
